import Container from 'components/helpers/Container';
import classnames from 'classnames';
import {
  TcDirectoryDiseaseStageProps,
  TcDirectoryDiseaseStatsProps,
  TcDirectoryListingProps,
} from '../_types';
import HeadingTag from 'components/helpers/HeadingTag';
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import { Field, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { logError, makeLoggableError } from 'lib/util/errors';
import fnNoOp from 'lib/util/no-op';
import { TcDirectoryListingClasses } from 'components/authorable/NmdpSite/TcDirectoryListing/_classes';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';

type DiseaseDetails = {
  displayName: string | undefined;
  stageName: string | undefined;
};
type DiseaseApiResponse = {
  Name: string;
};

const getDiseaseData = (
  parentDiseaseId: number | undefined,
  stageId: number | undefined,
  diseases: TcDirectoryDiseaseStatsProps
): DiseaseDetails => {
  let displayName: string | undefined;
  let stageName: string | undefined;

  if (parentDiseaseId || stageId) {
    const categories = ['related', 'unrelated', 'autologus'];

    const matchingItem = categories
      .map((category) => diseases?.[category] ?? [])
      .flat()
      .find((item) => item?.fields?.diseaseId.value === parentDiseaseId);

    if (matchingItem) {
      displayName = matchingItem.displayName;
      const matchingStage = matchingItem.fields?.stages?.find(
        (stage) => stage.fields?.diseaseId.value === stageId
      );
      stageName = matchingStage?.displayName;
    }
  }
  return { displayName, stageName };
};

const TransplantByDiseaseTab = ({ fields }: TcDirectoryListingProps): React.JSX.Element => {
  const { sitecoreContext: ctx } = useSitecoreContext();
  const { displayYears: rawDisplayYears } = fields?.listing ?? { displayYears: [] };
  const parentDiseaseId = !ctx.txSearchIds?.at(0) ? undefined : ctx.txSearchIds[0];
  const stageId = parentDiseaseId ? ctx.txSearchIds?.at(1) ?? 0 : undefined;
  const { displayName: incomingDiseaseName, stageName: incomingStageName } = getDiseaseData(
    parentDiseaseId,
    stageId,
    fields?.diseases as TcDirectoryDiseaseStatsProps
  );
  const [stageName, setStageName] = useState<string | undefined>(incomingStageName);
  const [diseaseName, setDiseaseName] = useState(
    incomingDiseaseName ? incomingDiseaseName : 'All Diseases'
  );
  const displayYears = rawDisplayYears
    .map((year: Sitecore.Override.ItemEx) => Number.parseInt(year.name ?? '0', 10))
    .filter((x) => !Number.isNaN(x) && x > 0)
    .sort((a, b) => a - b);
  let startYear: number;
  let endYear: number;
  if (displayYears.length < 2) {
    if (displayYears.length < 1) {
      startYear = new Date().getFullYear();
      endYear = startYear;
    } else {
      startYear = displayYears[0];
      endYear = startYear;
    }
  } else {
    startYear = displayYears[0];
    endYear = displayYears[displayYears.length - 1];
  }
  useEffect(() => {
    if (parentDiseaseId && stageName === undefined) {
      const apiUrl = `/api/sitecore/tcdirectory/disease?id=${parentDiseaseId}_${stageId ?? 0}`;
      const abortController = new AbortController();
      let requestProcessed = false;
      const diseaseData = async () => {
        const response = await axios.get<Array<DiseaseApiResponse>>(apiUrl);
        requestProcessed = true;
        const apiResponse = response.data?.at(0)?.Name;
        if (stageName === undefined && apiResponse) {
          if (stageId === 0) {
            setDiseaseName(apiResponse);
            setStageName('');
          } else {
            setStageName(apiResponse);
          }
        }
      };
      diseaseData().catch((error) => {
        requestProcessed = true;
        if (axios.isCancel(error)) {
          return;
        }
        if (axios.isAxiosError(error)) {
          logError('TransplantByDiseases:LoadData', error, apiUrl);
        } else {
          logError('TransplantByDiseases:LoadData', makeLoggableError(error), apiUrl);
        }
      });
      return () => {
        if (requestProcessed) {
          return;
        }
        abortController.abort();
      };
    }
    return fnNoOp;
  }, [diseaseName, stageName, stageId, parentDiseaseId]);
  const columnNames = [
    { id: '0', value: '' },
    { id: '1', value: '0-18' },
    { id: '2', value: '19-44' },
    { id: '3', value: '45-64' },
    { id: '4', value: '65+' },
    { id: 'total', value: 'Total' },
  ];
  const tdClass = classnames('p-2.5', 'border', 'border-gray-quartz');
  const trClass = classnames(
    'p-2.5',
    'even:bg-gray-whisper',
    'mb-2',
    'sm:mb-0',
    'border',
    'border-gray-quartz'
  );
  const thClass = classnames('p-2.5', 'border', 'border border-gray-quartz');

  const tableHeaders = columnNames.map((column) => {
    return (
      <Th key={column.id} className={thClass}>
        {column.value}
      </Th>
    );
  });

  const processDiseaseTotals = (allstage: boolean) => {
    const processStages = (stages: TcDirectoryDiseaseStageProps[]) => {
      /**
       * Read a numeric field value with a default of 0
       * @param {Field<number> | undefined} fld The field to read from
       * @returns The {@param Field.value}, or 0 if there is no field.
       */
      const getFieldValue = (fld?: Field<number>) => fld?.value ?? 0;
      // find the disease stage matching stageId if it's set, otherwise the general stage
      const findStageId = allstage ? 0 : stageId;
      const diseaseStage = stages?.find(
        (s) => (s.fields?.diseaseId?.value ?? -1) === findStageId
      ) ?? { fields: undefined };
      return [
        getFieldValue(diseaseStage.fields?.age1),
        getFieldValue(diseaseStage.fields?.age2),
        getFieldValue(diseaseStage.fields?.age3),
        getFieldValue(diseaseStage.fields?.age4),
        getFieldValue(diseaseStage.fields?.total),
      ];
    };

    const categories = ['related', 'unrelated', 'autologus'];
    const results = categories.map((category) => {
      const items = fields?.diseases?.[category] || [];
      let age1 = 0;
      let age2 = 0;
      let age3 = 0;
      let age4 = 0;
      let total = 0;

      items.forEach((item) => {
        if (
          !parentDiseaseId === null ||
          !parentDiseaseId ||
          item?.fields?.diseaseId?.value === parentDiseaseId
        ) {
          const [a1, a2, a3, a4, t] = processStages(item?.fields?.stages || []);

          age1 += a1;
          age2 += a2;
          age3 += a3;
          age4 += a4;
          total += t;
        }
      });

      return [age1, age2, age3, age4, total];
    });

    const overallTotal = results.reduce(
      (acc, [a1, a2, a3, a4, total]) => {
        acc[0] += a1;
        acc[1] += a2;
        acc[2] += a3;
        acc[3] += a4;
        acc[4] += total;
        return acc;
      },
      [0, 0, 0, 0, 0]
    );

    return [...results, overallTotal];
  };

  const buildCenterRows = (allstage: boolean) => {
    const rowData = [
      { key: 'related', value: 'Related' },
      { key: 'unrelated', value: 'Unrelated' },
      { key: 'autologus', value: 'Autologous' },
      { key: 'total', value: 'Total' },
    ];

    const diseaseData = processDiseaseTotals(allstage);

    return rowData.map((item) => {
      const data =
        item.key === 'total'
          ? diseaseData[3]
          : diseaseData[['related', 'unrelated', 'autologus'].indexOf(item.key)];
      return (
        <Tr key={item.key} className={trClass}>
          <Th className={tdClass}>{item.value}</Th>
          <Td className={tdClass}>{data[0]}</Td>
          <Td className={tdClass}>{data[1]}</Td>
          <Td className={tdClass}>{data[2]}</Td>
          <Td className={tdClass}>{data[3]}</Td>
          <Td className={tdClass}>{data[4]}</Td>
        </Tr>
      );
    });
  };

  return (
    <Container
      size="standard"
      dataComponent="authorable/nmdpsite/tcdirectory/TcDirectoryDiseaseStatsProps"
    >
      <>
        <div>
          <HeadingTag headingLevel="h3" className={TcDirectoryListingClasses.Heading.Medium}>
            {stageId === undefined ? diseaseName : stageName}
          </HeadingTag>
          <p className={TcDirectoryListingClasses.Paragraph}>
            Number of transplants by age reported from January 1, {startYear} to December 31st,{' '}
            {endYear}
            (includes marrow, PBSC and cord blood)
          </p>
        </div>
        <Table className="mb-4">
          <Thead>
            <Tr className={classnames('text-left', 'font-bold')}>{tableHeaders}</Tr>
          </Thead>
          <Tbody>{!parentDiseaseId ? buildCenterRows(true) : buildCenterRows(false)}</Tbody>
        </Table>

        {parentDiseaseId && stageId !== 0 ? (
          <>
            <br />
            <div>
              <HeadingTag headingLevel="h3" className={TcDirectoryListingClasses.Heading.Medium}>
                {diseaseName}
              </HeadingTag>
              <p className={TcDirectoryListingClasses.Paragraph}>
                Number of transplants by age reported from January 1, 2022 to December 31st, 2023
                (includes marrow, PBSC and cord blood)
              </p>
            </div>
            <Table className="mb-4">
              <Thead>
                <Tr className={classnames('text-left', 'font-bold')}>{tableHeaders}</Tr>
              </Thead>
              <Tbody>{buildCenterRows(true)}</Tbody>
            </Table>{' '}
          </>
        ) : (
          <></>
        )}

        <p className={TcDirectoryListingClasses.Paragraph}>
          Treatments may be similar for diseases within a group. It might be helpful to look at
          centers that have done transplants for a specific disease and centers that have done
          transplants for any <b>corresponding broad disease categories</b>.
        </p>
        <p className={TcDirectoryListingClasses.Paragraph}>
          Centers are not required to report autologous transplants so the numbers might be
          incomplete.
        </p>
        <p className={TcDirectoryListingClasses.Paragraph}>
          More information about transplants can be found:
        </p>
        <ul className={classnames('list-disc', 'list-inside')}>
          <li>
            {fields?.listing.txDataByCenterLink?.value?.href && (
              <LinkA11yWrapper
                className={classnames('text-blue-morning', 'underline')}
                field={fields?.listing.txDataByCenterLink}
              />
            )}{' '}
            report at blood cell.transplant.hrsa.gov
          </li>
          <li>
            {fields?.listing.diseaseListLink?.value?.href && (
              <LinkA11yWrapper
                className={classnames('text-blue-morning', 'underline')}
                field={fields?.listing.diseaseListLink}
              />
            )}
          </li>
        </ul>
      </>
    </Container>
  );
};
export default TransplantByDiseaseTab;
